import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { news } from "@/mocks/news";
import { newsCategories } from "@/mocks/categories";

const PER_PAGE = 5;

export default function News() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") || "全部";
  const [page, setPage] = useState(1);

  useDocumentMeta({
    title: "影视资讯 - 午夜场的电影资讯频道",
    description:
      "午夜场的电影资讯频道，提供影评、行业新闻、深夜观影专题与创作者访谈，帮你在选片之前了解午夜影视的最新动态。",
    keywords: "午夜电影资讯,影视资讯,午夜影评,午夜电影专题,午夜场的电影",
  });

  useEffect(() => {
    setPage(1);
  }, [categoryParam]);

  const filtered = useMemo(
    () =>
      categoryParam === "全部"
        ? news
        : news.filter((item) => item.category === categoryParam),
    [categoryParam],
  );

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const currentPage = Math.min(page, Math.max(totalPages, 1));
  const pageNews = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const changeCategory = (category: string) => {
    if (category === "全部") {
      searchParams.delete("category");
    } else {
      searchParams.set("category", category);
    }
    setSearchParams(searchParams);
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 lg:px-10">
      <header className="mb-8">
        <span className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary-400">
          <span className="h-1 w-6 rounded-full bg-primary-500" />
          News & Reviews
        </span>
        <h1 className="mb-3 font-heading text-3xl font-extrabold tracking-tight text-foreground-50 md:text-4xl">
          影视资讯
        </h1>
        <p className="max-w-3xl text-sm leading-relaxed text-foreground-500">
          汇集影评、行业新闻、深夜观影专题与创作者访谈，陪你了解午夜影视世界正在发生的事。
        </p>
      </header>

      <div className="mb-7 flex flex-wrap items-center gap-2 rounded-lg bg-background-100 p-1.5">
        {newsCategories.map((category) => {
          const active = category === categoryParam;
          return (
            <button
              key={category}
              type="button"
              onClick={() => changeCategory(category)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                active
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-400 hover:text-foreground-100"
              }`}
            >
              {category}
            </button>
          );
        })}
      </div>

      {pageNews.length > 0 ? (
        <>
          <div className="space-y-5">
            {pageNews.map((item, index) => (
              <NewsCard key={item.id} item={item} index={index} />
            ))}
          </div>
          <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
          <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-200/70 text-3xl text-foreground-500">
            <i className="ri-article-line"></i>
          </span>
          <h3 className="mb-2 font-heading text-lg font-bold text-foreground-100">
            该分类下暂时没有内容
          </h3>
          <p className="mb-6 max-w-sm text-sm text-foreground-600">
            换一个资讯分类看看，或前往首页浏览最新的午夜电影推荐。
          </p>
          <button
            type="button"
            onClick={() => changeCategory("全部")}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-refresh-line"></i>
            查看全部资讯
          </button>
        </div>
      )}
    </div>
  );
}